/** What the technician captures for a checklist item */
export type InspectionMediaType = 'photo' | 'video' | 'photo_video' | 'document';

/** `question.evidencePolicy.allowedKind` of an inspection template item */
export type InspectionEvidenceKind = 'PHOTO' | 'VIDEO' | 'PHOTO_VIDEO' | 'DOCUMENT';

export const MEDIA_TYPE_TO_EVIDENCE_KIND: Record<InspectionMediaType, InspectionEvidenceKind> = {
  photo: 'PHOTO',
  video: 'VIDEO',
  photo_video: 'PHOTO_VIDEO',
  document: 'DOCUMENT',
};

export const INSPECTION_MEDIA_OPTIONS: Array<{
  key: InspectionMediaType;
  label: string;
  icon: string;
  className: string;
}> = [
  { key: 'photo', label: 'Photo', icon: 'solar:camera-linear', className: 'text-blue-500' },
  { key: 'video', label: 'Video', icon: 'solar:videocamera-record-linear', className: 'text-orange-500' },
  { key: 'photo_video', label: 'Photo/Video', icon: 'solar:gallery-linear', className: 'text-green-500' },
  { key: 'document', label: 'Document', icon: 'solar:document-text-linear', className: 'text-purple-500' },
];

export const inspectionMediaOption = (key: InspectionMediaType) =>
  INSPECTION_MEDIA_OPTIONS.find((option) => option.key === key) ?? INSPECTION_MEDIA_OPTIONS[0];

/**
 * The capture type is stored in `question.evidencePolicy.allowedKind`. Items saved before that
 * field existed carry it as a `media:<type>` tag instead, which is still read and is dropped the
 * next time the item is saved. Items with neither are treated as photo items.
 */
const LEGACY_MEDIA_TAG_PREFIX = 'media:';
const DEFAULT_MEDIA_TYPE: InspectionMediaType = 'photo';

/** The parts of a `slm.inspection_template_item` record's `data` the media type is read from */
export interface InspectionItemMediaData {
  tags?: unknown;
  question?: { evidencePolicy?: { allowedKind?: unknown } };
}

export const isInspectionMediaType = (value: unknown): value is InspectionMediaType =>
  typeof value === 'string' && value in MEDIA_TYPE_TO_EVIDENCE_KIND;

function mediaTypeFromEvidenceKind(kind: unknown): InspectionMediaType | null {
  const match = Object.entries(MEDIA_TYPE_TO_EVIDENCE_KIND).find(([, value]) => value === kind);
  return match ? (match[0] as InspectionMediaType) : null;
}

export function mediaTypeFromLegacyTag(tags: unknown): InspectionMediaType | null {
  for (const tag of Array.isArray(tags) ? tags : []) {
    if (typeof tag !== 'string' || !tag.startsWith(LEGACY_MEDIA_TAG_PREFIX)) continue;
    const value = tag.slice(LEGACY_MEDIA_TAG_PREFIX.length);
    if (isInspectionMediaType(value)) return value;
  }
  return null;
}

export function getMediaType(data: InspectionItemMediaData | undefined): InspectionMediaType {
  return mediaTypeFromEvidenceKind(data?.question?.evidencePolicy?.allowedKind)
    ?? mediaTypeFromLegacyTag(data?.tags)
    ?? DEFAULT_MEDIA_TYPE;
}

/** Tags without the legacy media tag; every other tag is kept */
export function withoutLegacyMediaTag(tags: unknown): string[] {
  return (Array.isArray(tags) ? tags : []).filter(
    (tag): tag is string => typeof tag === 'string' && !tag.startsWith(LEGACY_MEDIA_TAG_PREFIX),
  );
}
